import type { BauordnungKontextEingabe, BauordnungKiVorschlag, ProjektEingabe } from '../../../services/aiAssistContext';
import type { ProjektSteckbrief } from '../../../services/projektSteckbrief/steckbrief';
import type { ServiceItem } from '../../../types';
import type { Handbuch } from '../../handbuch/types';
import type { Planungsphase, ProjektMerkmale } from '../../planungsphase/types';
import { istNichtFreigeschaltet, NICHT_FREIGESCHALTET_TITEL } from '../../sige-plan/kiAssistentLogic';
import { istGebucht, positionenFuer } from '../bauordnungBooking';
import { freigabeHindernisse, istBestaetigt, kapitelDesDokuments, type Hindernis } from '../bauordnungLogic';
import {
    automatikFehlt, ersetzePlatzhalter, fassungsIndex, kiFehlt, MERKMAL_KLAERUNG, merkmalAusVerweis,
    platzhalterAusVerweis, VERWEIS_ERLASSENDER, type AutomatikPaket,
} from '../bausteine/automatik';
import { bausteineDesKapitels, bausteinNachId } from '../bausteine/katalog';
import {
    GRUPPEN_LABEL, platzhalterDefinition, vorgabeImSatz, type PlatzhalterDefinition, type PlatzhalterGruppe,
} from '../bausteine/platzhalter';
import { kapitelDefinition, type BooleanMerkmal, type Dokumentart } from '../kapitel';
import type { Bauordnung, BauordnungBundle, Luecke, Regel, RegelArt } from '../types';
import { HANDBUCH_FELD_KAPITEL, HANDBUCH_FELD_LABEL, HANDBUCH_FELDER } from '../uebernahmeHandbuch';
import { grundlagenVorschlaege } from '../vorschlaege';

/**
 * Der naechste Schritt der GEFUEHRTEN Baustellenordnung (Plan
 * docs/baustellenordnung-assistenz-plan.md §3.2, Stufe B2) — als reine
 * Funktion aus dem Stand der Daten und dem, was der Mensch in dieser Sitzung
 * schon geklickt hat.
 *
 * Leitentscheid des Betreibers 02.11.2026: so automatisiert wie moeglich, die
 * Mitarbeiter sollen so wenig wie moeglich machen. Der Entwurf baut sich
 * deshalb selbst (Automatik, dann KI — beides im Container, ohne Klick), und
 * der Dialog zeigt nur noch, was ein Mensch entscheiden muss: „Stimmt so" zu
 * den KI-Ergaenzungen, die offenen Punkte, die kein Projektdatum kennt, und
 * die Freigabe.
 *
 * ═══ GRUPPIEREN STATT EINZELN FRAGEN ══════════════════════════════════════
 * Befund aus B1: ein leeres Projekt ergibt rund 70 Regelungen und gut 60
 * verschiedene Platzhalter. Einzeln gefragt waeren das 60 Karten. Deshalb:
 *   - EINE Frage je Platzhalter (`verweis`), auch wenn er in drei Regelungen
 *     steht — die Antwort setzt sich in allen dreien ein (`offeneFragen`);
 *   - EINE Karte fuer alle Vorgaben (z. B. „der Auftraggeber") mit einem „Stimmt so";
 *   - EINE Karte je Gruppe (Notfall, Zeiten, Orte …) mit mehreren Feldern;
 *   - je Frage „Regelung weglassen" als Ausweg, wenn sie hier nicht passt.
 *
 * ═══ WAS DIE SITZUNG HAELT ════════════════════════════════════════════════
 * „Später", „Weiß nicht", „Stimmt nicht" und „schon gesehen" liegen nur im
 * Speicher des Dialogs (`DialogSitzung`). Beim naechsten Oeffnen rechnet der
 * Dialog wieder aus den Daten — nichts davon wird geschrieben.
 *
 * Rein — kein DOM, keine Supabase. Was geschrieben wird, entscheidet der
 * Container (`BauordnungDialog.tsx`) ueber den Dienst.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SITZUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface DialogSitzung {
    /** Die KI soll in dieser Sitzung nicht (mehr) lesen — abgebrochen oder gescheitert. */
    kiAus: boolean;
    /** Warum die KI nicht lesen konnte — der Satz auf der Uebersicht. */
    kiFehler: string | null;
    /** Die Automatik scheiterte und der Mensch geht ohne sie weiter. */
    automatikAus: boolean;
    /** „Das steht schon drin" gesehen — fuer diesen Kopfsatz und diese Fassung (`uebersichtSchluessel`). */
    uebersichtGesehen: string | null;
    /** Gruppen mit „Später". */
    gruppenSpaeter: ReadonlySet<PlatzhalterGruppe>;
    /** „Später" auf der Karte der Vorgaben. */
    vorgabenSpaeter: boolean;
    /** „Weiß nicht" auf „Merkmal klären" — Ids der Luecken. */
    klaerungSpaeter: ReadonlySet<string>;
    /** KI-Zeilen mit „Stimmt nicht" (Regel-Ids). */
    verworfeneKi: ReadonlySet<string>;
    /** „Später" auf „Neu dazu". */
    neuDazuSpaeter: boolean;
    /** „Jemand anderes" auf „Wer erlässt?" — die Firma wird dann als Feld gefragt. */
    erlassenderVonHand: boolean;
}

export const LEERE_SITZUNG: DialogSitzung = {
    kiAus: false,
    kiFehler: null,
    automatikAus: false,
    uebersichtGesehen: null,
    gruppenSpaeter: new Set(),
    vorgabenSpaeter: false,
    klaerungSpaeter: new Set(),
    verworfeneKi: new Set(),
    neuDazuSpaeter: false,
    erlassenderVonHand: false,
};

/** Hat der Mensch in dieser Sitzung etwas uebersprungen, das wieder gezeigt werden kann? */
export const hatUebersprungen = (s: DialogSitzung): boolean =>
    s.gruppenSpaeter.size > 0 || s.vorgabenSpaeter || s.klaerungSpaeter.size > 0;

/** Alles Uebersprungene wieder zeigen — die Sitzung sonst unveraendert. */
export const ohneUebersprungene = (s: DialogSitzung): DialogSitzung => ({
    ...s, gruppenSpaeter: new Set(), vorgabenSpaeter: false, klaerungSpaeter: new Set(),
});

/** „Das steht schon drin" gilt je Kopfsatz und Fassung — nach „Neue Fassung eröffnen" kommt sie wieder. */
export const uebersichtSchluessel = (o: Pick<Bauordnung, 'id' | 'historie'>): string => `${o.id}:${fassungsIndex(o)}`;

// ═══════════════════════════════════════════════════════════════════════════
// EINGANG
// ═══════════════════════════════════════════════════════════════════════════

export interface DialogLage {
    projectId: string;
    bundle: BauordnungBundle;
    merkmale: ProjektMerkmale;
    nurAnsicht: boolean;
    /** Die Automatik schreibt gerade — oder wartet noch auf ihre Quellen. */
    automatikLaeuft: boolean;
    /** Der letzte Versuch der Automatik in dieser Sitzung ist gescheitert. */
    automatikFehlgeschlagen: boolean;
    kiLaeuft: boolean;
    /** `baueAutomatikEntwurf` auf den aktuellen Stand — `null`, solange die Quellen fehlen. */
    paket: AutomatikPaket | null;
    /** `platzhalterWerte` — was ein Projektdatum inzwischen fuellen kann (Vorbelegung). */
    werte: Readonly<Record<string, string>>;
    steckbrief: ProjektSteckbrief | null;
    buchung: { darfBuchen: boolean; services: readonly ServiceItem[] };
    sitzung: DialogSitzung;
}

// ═══════════════════════════════════════════════════════════════════════════
// OFFENE FRAGEN — je Platzhalter eine
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Frage = ein Platzhalter (`verweis`), egal in wie vielen Regelungen er steht. */
export interface OffeneFrage {
    verweis: string;
    /** Name des Platzhalters (`aushangort`). */
    name: string;
    definition: PlatzhalterDefinition;
    /** Die offenen Punkte dieses Platzhalters — einer je Regelung. */
    luecken: Luecke[];
    /** Die Regelungen, in denen er steht (ohne Doppelte). */
    regeln: Regel[];
    /** Die Vorgabe im richtigen Fall je Regel („der Bauleitung" nach „bei") — nur mit Vorgabe. */
    vorgabeJeRegel?: Record<string, string>;
}

/**
 * Reihenfolge der Gruppen: wer zustaendig ist zuerst (eine Karte mit
 * Vorgaben), dann was nur der Mensch weiss — Notfall vor Zeiten vor Orten —,
 * die Projektdaten zuletzt (die meisten fuellt die Automatik).
 */
export const GRUPPEN_FOLGE: readonly PlatzhalterGruppe[] = [
    'verantwortliche', 'organisation', 'notfall', 'zeiten', 'orte', 'regelung', 'projekt',
];

/**
 * Platzhalter, die zum Kopfsatz gehoeren: der Erlassende und sein
 * Unterzeichner. Sie werden nicht als Luecke gefragt, sondern auf der Karte
 * „Hindernis" als Feld des Kopfsatzes — sonst stuende die Antwort im Text,
 * aber nicht am Kopfsatz, und die Freigabe fragte ein zweites Mal.
 */
export const KOPF_PLATZHALTER: ReadonlySet<string> = new Set(['erlassender', 'unterzeichner']);

const VORGABE_GRUPPEN: ReadonlySet<PlatzhalterGruppe> = new Set(['verantwortliche', 'organisation']);

const rangDerGruppe = (g: PlatzhalterGruppe): number => {
    const i = GRUPPEN_FOLGE.indexOf(g);
    return i < 0 ? GRUPPEN_FOLGE.length : i;
};

/**
 * Die offenen Punkte mit Platzhalter, zu Fragen gebuendelt: gleicher
 * `verweis` → EINE Frage mit allen Luecken und Regelungen. Sortiert nach
 * Gruppe (`GRUPPEN_FOLGE`), dann nach der kleinsten Nummer.
 */
export const offeneFragen = (bundle: Pick<BauordnungBundle, 'luecken' | 'regeln'>): OffeneFrage[] => {
    const nachId = new Map(bundle.regeln.map(r => [r.id, r]));
    const jeVerweis = new Map<string, OffeneFrage>();
    const ersteNr = new Map<string, number>();
    for (const l of [...bundle.luecken].sort((a, b) => a.nr - b.nr)) {
        if (l.erledigt_am) continue;
        const name = platzhalterAusVerweis(l.verweis);
        if (!name) continue;
        let frage = jeVerweis.get(l.verweis);
        if (!frage) {
            frage = { verweis: l.verweis, name, definition: platzhalterDefinition(name), luecken: [], regeln: [] };
            jeVerweis.set(l.verweis, frage);
            ersteNr.set(l.verweis, l.nr);
        }
        frage.luecken.push(l);
        const r = l.regel_id ? nachId.get(l.regel_id) : undefined;
        if (r && !frage.regeln.some(x => x.id === r.id)) frage.regeln.push(r);
    }
    const fragen = Array.from(jeVerweis.values()).map(f => {
        if (!f.definition.vorgabe) return f;
        const vorgabeJeRegel: Record<string, string> = {};
        for (const r of f.regeln) vorgabeJeRegel[r.id] = vorgabeImSatz(f.name, r.wortlaut) ?? f.definition.vorgabe;
        return { ...f, vorgabeJeRegel };
    });
    return fragen.sort((a, b) =>
        (rangDerGruppe(a.definition.gruppe) - rangDerGruppe(b.definition.gruppe))
        || ((ersteNr.get(a.verweis) ?? 0) - (ersteNr.get(b.verweis) ?? 0)));
};

/** Die Fragen der Karte „Verantwortliche": Gruppen mit Vorgabe, ohne Kopf-Platzhalter. */
export const vorgabeFragen = (fragen: readonly OffeneFrage[]): OffeneFrage[] =>
    fragen.filter(f => VORGABE_GRUPPEN.has(f.definition.gruppe) && !!f.definition.vorgabe && !KOPF_PLATZHALTER.has(f.name));

/** Die Fragen einer Gruppenkarte — ohne die mit Vorgabe (die stehen auf ihrer eigenen Karte) und ohne Kopf-Platzhalter. */
export const gruppenFragen = (fragen: readonly OffeneFrage[], gruppe: PlatzhalterGruppe): OffeneFrage[] =>
    fragen.filter(f => f.definition.gruppe === gruppe
        && !(VORGABE_GRUPPEN.has(gruppe) && f.definition.vorgabe)
        && !KOPF_PLATZHALTER.has(f.name));

// ═══════════════════════════════════════════════════════════════════════════
// ANTWORTEN — in welcher Folge der Dienst schreibt
// ═══════════════════════════════════════════════════════════════════════════

/** Ein Aufruf von `beantworteLuecken`: Luecken derselben Frage, die Regeln im JETZIGEN Wortlaut, die Antwort. */
export interface AntwortSchritt {
    luecken: Luecke[];
    regeln: Regel[];
    antwort: string;
}

const gleich = (a: string, b: string): boolean => a.trim() === b.trim();

/**
 * Die Aufrufe fuer eine Karte voller Antworten — rein.
 *
 * Zwei Fallen, die hier abgefangen werden:
 *   1. Der Dienst ersetzt nur, wenn der Wortlaut noch so lautet, wie er
 *      gelesen wurde (`eq('wortlaut', …)` im Update). Stehen ZWEI Platzhalter
 *      einer Karte in derselben Regel („von {arbeitszeit_beginn} bis
 *      {arbeitszeit_ende}"), traefe der zweite Aufruf sonst den alten
 *      Wortlaut und scheiterte. Deshalb wird der Wortlaut nach jedem Schritt
 *      hier fortgeschrieben, und jeder Schritt bekommt den neuen.
 *   2. Die unveraenderte Vorgabe steht je Regel im richtigen Fall („der
 *      Bauleitung" nach „bei", `vorgabeImSatz`) — also je Fall ein Aufruf.
 *      Eine geaenderte Eingabe gilt woertlich fuer alle Regeln der Frage.
 *
 * Leere Eingaben fehlen: die Frage bleibt offen.
 */
export const planeAntworten = (
    fragen: readonly OffeneFrage[],
    eingaben: Readonly<Record<string, string>>,
    regeln: readonly Regel[],
): AntwortSchritt[] => {
    const aktuell = new Map(regeln.map(r => [r.id, r]));
    const jetzt = (r: Regel): Regel => aktuell.get(r.id) ?? r;
    const schritte: AntwortSchritt[] = [];
    const fortschreiben = (s: AntwortSchritt, name: string) => {
        for (const r of s.regeln) aktuell.set(r.id, { ...r, wortlaut: ersetzePlatzhalter(r.wortlaut, name, s.antwort) });
    };

    for (const f of fragen) {
        const eingabe = String(eingaben[f.verweis] ?? '').trim();
        if (!eingabe) continue;
        const vorgabe = f.definition.vorgabe;
        if (!vorgabe || !gleich(eingabe, vorgabe)) {
            const s: AntwortSchritt = { luecken: [...f.luecken], regeln: f.regeln.map(jetzt), antwort: eingabe };
            schritte.push(s);
            fortschreiben(s, f.name);
            continue;
        }
        // Unveraenderte Vorgabe: je Fall im Satz ein Aufruf.
        const jeAntwort = new Map<string, AntwortSchritt>();
        const nachRegel = new Map(f.regeln.map(r => [r.id, r]));
        for (const l of f.luecken) {
            const r = l.regel_id ? nachRegel.get(l.regel_id) : undefined;
            const antwort = r ? (vorgabeImSatz(f.name, jetzt(r).wortlaut) ?? eingabe) : eingabe;
            let s = jeAntwort.get(antwort);
            if (!s) {
                s = { luecken: [], regeln: [], antwort };
                jeAntwort.set(antwort, s);
            }
            s.luecken.push(l);
            if (r && !s.regeln.some(x => x.id === r.id)) s.regeln.push(jetzt(r));
        }
        for (const s of jeAntwort.values()) {
            schritte.push(s);
            fortschreiben(s, f.name);
        }
    }
    return schritte;
};

// ═══════════════════════════════════════════════════════════════════════════
// ZUSAMMENFASSUNG, UEBERSICHT, NEU DAZU
// ═══════════════════════════════════════════════════════════════════════════

/** Die Dokumente, die der Kopfsatz erzeugt. */
const dokumenteVon = (o: Pick<Bauordnung, 'erzeugt_ordnung' | 'erzeugt_hse'>): Dokumentart[] => {
    const arten: Dokumentart[] = [];
    if (o.erzeugt_ordnung) arten.push('ordnung');
    if (o.erzeugt_hse) arten.push('hse');
    return arten;
};

/**
 * Kapitel, die die App selbst ableitet (Deckblatt, Revisionsverzeichnis,
 * offene Punkte) — sie tragen keine Regelungen und werden weder gezaehlt
 * noch der KI gezeigt.
 */
const nurAbgeleitet = (kapitel: string): boolean => kapitelDefinition(kapitel)?.quelle === 'abgeleitet';

interface KapitelEintrag { id: string; titel: string; dokument: Dokumentart; pflicht: boolean }

/** Die gezogenen, nicht ausgeschlossenen Kapitel aller erzeugten Dokumente — je Kennung einmal. */
const kapitelListe = (bundle: BauordnungBundle, merkmale: ProjektMerkmale): KapitelEintrag[] => {
    const o = bundle.ordnung;
    if (!o) return [];
    const ausgeschlossen = new Set(bundle.abschnitte.filter(a => !a.gezogen).map(a => a.kapitel));
    const gesehen = new Set<string>();
    const liste: KapitelEintrag[] = [];
    for (const art of dokumenteVon(o)) {
        for (const g of kapitelDesDokuments(bundle, art, merkmale)) {
            const id = g.definition.id;
            if (gesehen.has(id) || ausgeschlossen.has(id) || nurAbgeleitet(id)) continue;
            gesehen.add(id);
            liste.push({ id, titel: g.definition.titel, dokument: art, pflicht: g.pflicht });
        }
    }
    return liste;
};

export interface Zusammenfassung {
    /** Kapitel mit mindestens einer bestaetigten Regelung. */
    kapitel: number;
    /** Bestaetigte Regelungen — nur die stehen im Dokument. */
    regelungen: number;
    rollen: number;
    /** Offene Pflichtpunkte. */
    offenePunkte: number;
    /** KI-Vorschlaege ohne Bestaetigung — stehen nicht im Dokument. */
    kiOffen: number;
}

export const zusammenfassung = (bundle: BauordnungBundle, merkmale: ProjektMerkmale): Zusammenfassung => {
    const kapitel = new Set(kapitelListe(bundle, merkmale).map(k => k.id));
    const bestaetigt = bundle.regeln.filter(r => istBestaetigt(r) && kapitel.has(r.kapitel));
    return {
        kapitel: new Set(bestaetigt.map(r => r.kapitel)).size,
        regelungen: bestaetigt.length,
        rollen: bundle.rollen.length,
        offenePunkte: bundle.luecken.filter(l => l.pflicht && !l.erledigt_am).length,
        kiOffen: bundle.regeln.filter(r => r.quelle === 'ki' && !r.bestaetigt_am).length,
    };
};

export interface UebersichtKapitel {
    kapitel: string;
    titel: string;
    bestaetigt: number;
    herkunft: { katalog: number; handbuch: number; ki: number; frei: number };
}

export interface KiZeile {
    id: string;
    wortlaut: string;
    art: RegelArt;
    /** „Stimmt nicht" in dieser Sitzung. */
    verworfen: boolean;
}

export interface KiGruppe {
    kapitel: string;
    titel: string;
    zeilen: KiZeile[];
}

export interface Uebersicht {
    kapitel: UebersichtKapitel[];
    zusammen: Zusammenfassung;
    /** Merkmale, die noch zu klaeren sind. */
    zuKlaeren: number;
    /** Was die Automatik nicht aufgenommen hat, mit Grund — aufklappbar. */
    weggelassen: { titel: string; grund: string }[];
    /** Die KI-Ergaenzungen ohne Bestaetigung, je Kapitel. */
    ki: KiGruppe[];
    /** Ein Satz, wenn die KI nicht lesen konnte oder abgebrochen wurde. */
    kiHinweis: string | null;
}

export const KI_NICHT_ERREICHBAR =
    'KI nicht erreichbar. Der Entwurf ist auch ohne sie vollständig; beim nächsten Öffnen wird es erneut versucht.';
export const KI_NICHT_FREIGESCHALTET =
    `${NICHT_FREIGESCHALTET_TITEL}. Der Entwurf ist auch ohne sie vollständig.`;
export const KI_ABGEBROCHEN =
    'Die KI-Durchsicht wurde abgebrochen. Beim nächsten Öffnen wird es erneut versucht.';

/** Der Satz zur KI auf der Uebersicht — oder `null`, wenn sie gelesen hat. */
export const kiHinweis = (o: Bauordnung, s: Pick<DialogSitzung, 'kiAus' | 'kiFehler'>): string | null => {
    if (s.kiFehler) return istNichtFreigeschaltet(s.kiFehler) ? KI_NICHT_FREIGESCHALTET : KI_NICHT_ERREICHBAR;
    if (s.kiAus && kiFehlt(o)) return KI_ABGEBROCHEN;
    return null;
};

const titelDesKapitels = (kapitel: string): string => kapitelDefinition(kapitel)?.titel ?? kapitel;

export const uebersicht = (l: DialogLage): Uebersicht | null => {
    const o = l.bundle.ordnung;
    if (!o) return null;
    const liste = kapitelListe(l.bundle, l.merkmale);
    const kapitel: UebersichtKapitel[] = liste.map(k => {
        const herkunft = { katalog: 0, handbuch: 0, ki: 0, frei: 0 };
        let bestaetigt = 0;
        for (const r of l.bundle.regeln) {
            if (r.kapitel !== k.id || !istBestaetigt(r)) continue;
            bestaetigt += 1;
            const quelle = (r.quelle in herkunft ? r.quelle : 'frei') as keyof typeof herkunft;
            herkunft[quelle] += 1;
        }
        return { kapitel: k.id, titel: k.titel, bestaetigt, herkunft };
    });

    const reihenfolge = new Map(liste.map((k, i) => [k.id, i]));
    const jeKapitel = new Map<string, KiGruppe>();
    const kiRegeln = l.bundle.regeln
        .filter(r => r.quelle === 'ki' && !r.bestaetigt_am && r.wortlaut.trim())
        .sort((a, b) => ((reihenfolge.get(a.kapitel) ?? 999) - (reihenfolge.get(b.kapitel) ?? 999)) || (a.sortierung - b.sortierung));
    for (const r of kiRegeln) {
        let g = jeKapitel.get(r.kapitel);
        if (!g) {
            g = { kapitel: r.kapitel, titel: titelDesKapitels(r.kapitel), zeilen: [] };
            jeKapitel.set(r.kapitel, g);
        }
        g.zeilen.push({ id: r.id, wortlaut: r.wortlaut, art: r.art, verworfen: l.sitzung.verworfeneKi.has(r.id) });
    }

    return {
        kapitel,
        zusammen: zusammenfassung(l.bundle, l.merkmale),
        zuKlaeren: l.bundle.luecken.filter(x => !x.erledigt_am && merkmalAusVerweis(x.verweis)).length,
        weggelassen: (l.paket?.weggelassen ?? []).map(w => ({ titel: bausteinNachId(w.baustein)?.titel ?? w.baustein, grund: w.grund })),
        ki: Array.from(jeKapitel.values()),
        kiHinweis: kiHinweis(o, l.sitzung),
    };
};

/** Die KI-Zeilen, die „Stimmt so" bestaetigt bzw. loescht. */
export const kiAuswahl = (u: Pick<Uebersicht, 'ki'>): { bestaetigen: string[]; loeschen: string[] } => {
    const bestaetigen: string[] = [];
    const loeschen: string[] = [];
    for (const g of u.ki) for (const z of g.zeilen) (z.verworfen ? loeschen : bestaetigen).push(z.id);
    return { bestaetigen, loeschen };
};

export interface NeuDazuZeile {
    bausteinId: string;
    titel: string;
    kapitelTitel: string;
    wortlaut: string;
    /** „Kran: jetzt ja" — oder „neu im Katalog". */
    grund: string;
}

/** Die Nachzuegler der Automatik mit Grund. */
export const neuDazuZeilen = (paket: AutomatikPaket, merkmale: ProjektMerkmale): NeuDazuZeile[] => {
    const m = (merkmale || {}) as Record<string, unknown>;
    return paket.regeln.map(r => {
        const b = bausteinNachId(r.baustein_id);
        const treffer = (b?.wenn ?? []).filter(k => m[k] === true);
        return {
            bausteinId: r.baustein_id,
            titel: b?.titel ?? r.baustein_id,
            kapitelTitel: titelDesKapitels(r.kapitel),
            wortlaut: r.wortlaut,
            grund: treffer.length
                ? treffer.map(k => `${MERKMAL_KLAERUNG[k]?.kurz ?? k}: jetzt ja`).join(', ')
                : 'neu im Katalog',
        };
    });
};

// ═══════════════════════════════════════════════════════════════════════════
// KOPFSATZ, HINDERNIS, BUCHUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface ErlassenderWahl {
    id: 'erlassender_bauherr' | 'erlassender_dritter';
    /** „Bauherr" bzw. die Rolle des Dritten. */
    rolle: string;
    firma: string;
    patch: Partial<Bauordnung>;
}

/** Die beiden Kandidaten aus dem Steckbrief — Bauherr und Dritter nach § 4. */
export const erlassenderWahl = (sb: ProjektSteckbrief | null, o: Bauordnung): ErlassenderWahl[] =>
    grundlagenVorschlaege(sb, { ...o, erlassender_firma: '', erlassender_rolle: null })
        .filter(v => v.feld === 'erlassender')
        .map(v => ({
            id: v.id as ErlassenderWahl['id'],
            rolle: v.id === 'erlassender_bauherr'
                ? 'Bauherr'
                : (String(sb?.dritter.rolle.wert ?? '').trim() || 'Dritter nach § 4 BaustellV'),
            firma: String(v.patch.erlassender_firma ?? '').trim(),
            patch: v.patch,
        }))
        .filter(w => w.firma);

export interface KopfFeld {
    zeigen: boolean;
    wert: string;
}

/** Erlassender, Unterzeichner, Funktion — direkt auf der Karte. */
export interface KopfFelder {
    firma: KopfFeld;
    name: KopfFeld;
    funktion: KopfFeld;
    /** Rolle, die mit der vorbelegten Firma kaeme (aus dem Steckbrief) — nur wenn die Firma so bleibt. */
    rolleZurVorbelegung: Partial<Bauordnung> | null;
    /** Offene Punkte am Kopfsatz: `kopf:erlassender`, `platzhalter:erlassender`, `platzhalter:unterzeichner`. */
    luecken: Luecke[];
}

const KOPF_VERWEISE = new Set([VERWEIS_ERLASSENDER, 'platzhalter:erlassender', 'platzhalter:unterzeichner']);

export const kopfFelder = (l: Pick<DialogLage, 'bundle' | 'werte' | 'steckbrief'>): KopfFelder | null => {
    const o = l.bundle.ordnung;
    if (!o) return null;
    const luecken = l.bundle.luecken.filter(x => !x.erledigt_am && KOPF_VERWEISE.has(x.verweis));
    const offen = (verweise: string[]) => luecken.some(x => verweise.includes(x.verweis));
    const firmaZeigen = !o.erlassender_firma.trim() || offen([VERWEIS_ERLASSENDER, 'platzhalter:erlassender']);
    const nameZeigen = !o.erlassender_name.trim() || offen(['platzhalter:unterzeichner']);
    if (!firmaZeigen && !nameZeigen) return null;

    let firma = o.erlassender_firma.trim();
    let rolleZurVorbelegung: Partial<Bauordnung> | null = null;
    if (!firma) {
        const wahl = erlassenderWahl(l.steckbrief, o);
        if (wahl.length === 1) {
            firma = wahl[0].firma;
            rolleZurVorbelegung = wahl[0].patch.erlassender_rolle ? { erlassender_rolle: wahl[0].patch.erlassender_rolle } : null;
        }
    }
    return {
        firma: { zeigen: firmaZeigen, wert: firma },
        name: { zeigen: nameZeigen, wert: o.erlassender_name.trim() },
        funktion: { zeigen: nameZeigen, wert: o.erlassender_funktion.trim() },
        rolleZurVorbelegung,
        luecken,
    };
};

/**
 * Was „Weiter" auf den Kopffeldern schreibt: nur gezeigte, geaenderte Felder;
 * Firma und Name nie leer. Bleibt die vorbelegte Firma aus dem Steckbrief
 * stehen, kommt ihre Rolle mit. Dazu die Antworten fuer die Platzhalter
 * `erlassender`/`unterzeichner` — mit dem, was danach am Kopfsatz steht.
 */
export const kopfAenderung = (
    o: Bauordnung,
    k: KopfFelder,
    e: { firma: string; name: string; funktion: string },
): { patch: Partial<Bauordnung>; eingaben: Record<string, string>; erlassenderErledigt: boolean } => {
    const firma = e.firma.trim();
    const name = e.name.trim();
    const funktion = e.funktion.trim();
    const patch: Partial<Bauordnung> = {};
    if (k.firma.zeigen && firma && firma !== o.erlassender_firma.trim()) {
        patch.erlassender_firma = firma;
        if (k.rolleZurVorbelegung && firma === k.firma.wert) Object.assign(patch, k.rolleZurVorbelegung);
    }
    if (k.name.zeigen && name && name !== o.erlassender_name.trim()) patch.erlassender_name = name;
    if (k.funktion.zeigen && funktion !== o.erlassender_funktion.trim()) patch.erlassender_funktion = funktion;
    const firmaDanach = String(patch.erlassender_firma ?? o.erlassender_firma).trim();
    const nameDanach = String(patch.erlassender_name ?? o.erlassender_name).trim();
    const eingaben: Record<string, string> = {};
    if (firmaDanach) eingaben['platzhalter:erlassender'] = firmaDanach;
    if (nameDanach) eingaben['platzhalter:unterzeichner'] = nameDanach;
    return { patch, eingaben, erlassenderErledigt: !!firmaDanach };
};

/**
 * Was der Freigabe noch entgegensteht, ohne Kopfsatz und ohne offene Punkte —
 * die haben eigene Karten. Platzhalter in einer Regel mit offenem
 * PFLICHT-Punkt zaehlen hier nicht: sie verschwinden mit der Antwort. Ein
 * Punkt ohne Pflicht haelt die Freigabe nicht an — sein Platzhalter im Text
 * aber schon, deshalb bleibt der hier stehen.
 */
export const restHindernisse = (bundle: BauordnungBundle, merkmale: ProjektMerkmale): Hindernis[] => {
    const o = bundle.ordnung;
    if (!o) return [];
    const mitOffenerLuecke = new Set(bundle.luecken
        .filter(l => l.pflicht && !l.erledigt_am && l.regel_id)
        .map(l => String(l.regel_id)));
    const ohne: BauordnungBundle = {
        ...bundle,
        ordnung: { ...o, erlassender_firma: o.erlassender_firma.trim() || 'gesetzt', erlassender_name: o.erlassender_name.trim() || 'gesetzt' },
        luecken: [],
        regeln: bundle.regeln.map(r => (mitOffenerLuecke.has(r.id)
            ? { ...r, wortlaut: r.wortlaut.replace(/\[[^\]\n]{1,60}\]|\{[a-z0-9_]+\}/g, '…') }
            : r)),
    };
    return freigabeHindernisse(ohne, merkmale);
};

export type BuchungsHaken =
    /** Genau eine Position passt — der Haken steht vorgesetzt. */
    | { art: 'haken'; position: { id: string; text: string } }
    /** Mehrere Positionen (oder auch der HSE-Plan): die Abrechnung fragt nach der Freigabe nach. */
    | { art: 'nachfrage' }
    /** Nichts zu buchen: gebucht, keine Position, oder hier nicht erlaubt. */
    | { art: 'keiner' };

/**
 * Der Buchungshaken der Freigabe (Plan E9). Vorgesetzt nur, wenn GENAU eine
 * Position der Baustellenordnung passt — die Katalogposition oder die einzige
 * `site_rules`-Position. Erzeugt der Kopfsatz auch den HSE-Plan, bleibt die
 * Buchung beim Abschlussdialog: dort stehen beide Dokumente.
 */
export const buchungsHaken = (
    o: Bauordnung,
    services: readonly ServiceItem[],
    darfBuchen: boolean,
): BuchungsHaken => {
    if (!darfBuchen || !o.erzeugt_ordnung || istGebucht(o, 'ordnung')) return { art: 'keiner' };
    const p = positionenFuer(services, 'ordnung');
    const einzige = p.vorgeschlagen ?? (p.auswahl.length === 1 ? p.auswahl[0] : null);
    if (!einzige) return p.auswahl.length > 1 ? { art: 'nachfrage' } : { art: 'keiner' };
    if (o.erzeugt_hse) return { art: 'nachfrage' };
    return { art: 'haken', position: { id: einzige.id, text: `${einzige.lvPosition || ''} ${einzige.title || ''}`.trim() } };
};

// ═══════════════════════════════════════════════════════════════════════════
// KI — Kontext und Zeilen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Eingabe fuer `baueBauordnungKontext` — nur, was der Kontextbauer
 * verlangt; ausgefiltert wird dort (`ohnePersonendaten`). Kapitel ohne die
 * abgeleiteten, Bausteintitel aus dem Katalog, Rollen nur als Bezeichnung,
 * Geltungsort/Sammelplatz/Zugang nur als ja/nein.
 *
 * ═══ WELCHE WORTLAUTE MITGEHEN (Codex-Review 02.11.2026, P1) ══════════════
 * Nur FREI formulierte und fruehere KI-Regeln gehen im Wortlaut mit.
 * Katalogregeln NICHT: die Automatik hat ihre Platzhalter gefuellt — mit
 * Krankenhaus und Anschrift aus dem Notrufplan, Sammelplatz und Zeiten aus
 * dem Handbuch, Rufnummern und Stellen aus den Antworten des Menschen. Das
 * sind genau die Angaben, die Spec F.2 vom Modell fernhaelt, und
 * `ohnePersonendaten` kennt weder Firmen noch Anschriften. Handbuch-Zeilen
 * ebenso nicht (Zugang und Sammelplatz stehen dort im Wortlaut). Das Modell
 * erfaehrt stattdessen je Kapitel die ZAHL der bestaetigten Regelungen und
 * die Titel aller Katalogbausteine — damit weiss es, was geregelt ist, ohne
 * den Text zu sehen.
 */
export const kiKontextEingabe = (a: {
    bundle: BauordnungBundle;
    merkmale: ProjektMerkmale;
    projekt: ProjektEingabe | null;
    planung: Planungsphase | null;
    handbuch: Pick<Handbuch, 'ordnung' | 'org_modell'> | null;
    gewerke: string[];
}): BauordnungKontextEingabe => {
    const o = a.bundle.ordnung;
    const kapitel = kapitelListe(a.bundle, a.merkmale);
    const ids = new Set(kapitel.map(k => k.id));
    const regelnJeKapitel: Record<string, string[]> = {};
    const regelnAnzahlJeKapitel: Record<string, number> = {};
    const bausteinTitelJeKapitel: Record<string, string[]> = {};
    for (const k of kapitel) {
        regelnJeKapitel[k.id] = [];
        regelnAnzahlJeKapitel[k.id] = 0;
        bausteinTitelJeKapitel[k.id] = bausteineDesKapitels(k.id).map(b => b.titel);
    }
    // Regeln, zu denen je ein offener Punkt gehoerte (auch erledigt — Punkte
    // werden nie geloescht), hatten einen Platzhalter; was heute dort steht,
    // ist die Antwort des Menschen (Rufnummer, Stelle, Name) und bleibt im
    // Haus, gleich welcher Quelle die Regel ist (Opus-Review 02.11.2026).
    const mitPlatzhalter = new Set(a.bundle.luecken.map(l => l.regel_id).filter((id): id is string => !!id));
    const handbuchKapitel = new Set<string>();
    for (const r of a.bundle.regeln) {
        if (!ids.has(r.kapitel) || !istBestaetigt(r)) continue;
        regelnAnzahlJeKapitel[r.kapitel] += 1;
        if (r.quelle === 'handbuch') handbuchKapitel.add(r.kapitel);
        if ((r.quelle === 'frei' || r.quelle === 'ki') && !mitPlatzhalter.has(r.id)) regelnJeKapitel[r.kapitel].push(r.wortlaut.trim());
    }
    // Handbuch-Zeilen gehen weder im Wortlaut noch als Baustein mit — damit das
    // Modell Zugang, Sammelplatz und Arbeitszeiten nicht erneut vorschlaegt,
    // stehen die Handbuchfelder des Kapitels als Titel neben den Bausteinen.
    for (const feld of HANDBUCH_FELDER) {
        const kapitelId = HANDBUCH_FELD_KAPITEL[feld];
        if (handbuchKapitel.has(kapitelId) && bausteinTitelJeKapitel[kapitelId]) {
            bausteinTitelJeKapitel[kapitelId].push(`Aus dem Handbuch: ${HANDBUCH_FELD_LABEL[feld]}`);
        }
    }
    const offen = (name: string) => a.bundle.luecken.some(l => !l.erledigt_am && l.verweis === `platzhalter:${name}`);
    const getragen = (name: string) => a.bundle.regeln.some(r =>
        istBestaetigt(r) && !!r.baustein_id && (bausteinNachId(r.baustein_id)?.platzhalter ?? []).includes(name));
    return {
        projekt: a.projekt,
        planung: a.planung,
        merkmale: a.merkmale,
        orgModell: o?.org_modell ?? a.handbuch?.org_modell ?? null,
        kapitel: kapitel.map(k => ({ id: k.id, titel: k.titel, dokument: k.dokument, pflicht: k.pflicht })),
        regelnJeKapitel,
        regelnAnzahlJeKapitel,
        bausteinTitelJeKapitel,
        rollenFunktionen: a.bundle.rollen.map(r => r.rolle.trim() || r.funktion.trim()).filter(Boolean),
        erfasst: {
            geltungsort: !!o?.geltung_ort.trim(),
            sammelplatz: getragen('sammelplatz') && !offen('sammelplatz'),
            zugang: !!String(a.handbuch?.ordnung?.zugang ?? '').trim(),
        },
        gewerke: a.gewerke,
    };
};

export interface KiRegelZeile {
    kapitel: string;
    wortlaut: string;
    art: RegelArt;
    sortierung: number;
}

const vergleichsText = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Die KI-Vorschlaege als neue Regelzeilen: hinter die vorhandenen des
 * Kapitels einsortiert, ohne leere und ohne Wortgleiches (schon da oder
 * zweimal vorgeschlagen).
 */
export const kiRegelZeilen = (
    vorschlaege: readonly Pick<BauordnungKiVorschlag, 'kapitel' | 'wortlaut' | 'art'>[],
    regeln: readonly Pick<Regel, 'kapitel' | 'wortlaut' | 'sortierung'>[],
): KiRegelZeile[] => {
    const naechste = new Map<string, number>();
    const vorhanden = new Set<string>();
    for (const r of regeln) {
        naechste.set(r.kapitel, Math.max(naechste.get(r.kapitel) ?? 0, (r.sortierung ?? 0) + 1));
        vorhanden.add(`${r.kapitel}|${vergleichsText(r.wortlaut)}`);
    }
    const zeilen: KiRegelZeile[] = [];
    for (const v of vorschlaege) {
        const wortlaut = String(v.wortlaut || '').trim();
        const schluessel = `${v.kapitel}|${vergleichsText(wortlaut)}`;
        if (!wortlaut || !v.kapitel || vorhanden.has(schluessel)) continue;
        vorhanden.add(schluessel);
        const sortierung = naechste.get(v.kapitel) ?? 0;
        naechste.set(v.kapitel, sortierung + 1);
        zeilen.push({ kapitel: v.kapitel, wortlaut, art: v.art === 'koordinierungshinweis' ? 'koordinierungshinweis' : 'regel', sortierung });
    }
    return zeilen;
};

/** Der Historie-Eintrag nach einem KI-Lauf — beginnt mit dem Marker (`kiMarker`). */
export const kiHistorie = (marker: string, vorschlaege: number, verworfen: number): string => {
    const zusatz = verworfen ? `, ${verworfen} verworfen` : '';
    return vorschlaege
        ? `${marker} · ${vorschlaege} ${vorschlaege === 1 ? 'Vorschlag' : 'Vorschläge'}${zusatz}`
        : `${marker} · nichts zu ergänzen${zusatz}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// DER NAECHSTE SCHRITT
// ═══════════════════════════════════════════════════════════════════════════

export type DialogSchritt =
    | { art: 'laden' }
    /** Kein Kopfsatz. In der Nur-Ansicht ohne Knopf. */
    | { art: 'anlegen'; nurAnsicht: boolean }
    | { art: 'automatik_laeuft'; fehlgeschlagen: boolean }
    | { art: 'ki_liest' }
    | { art: 'freigegeben'; zusammen: Zusammenfassung }
    | { art: 'nur_ansicht'; zusammen: Zusammenfassung }
    | { art: 'uebersicht'; uebersicht: Uebersicht; schluessel: string }
    | { art: 'neu_dazu'; zeilen: NeuDazuZeile[] }
    | { art: 'erlassender'; luecke: Luecke; wahl: ErlassenderWahl[] }
    | {
        art: 'klaerung';
        luecke: Luecke;
        merkmal: BooleanMerkmal;
        frage: string;
        kurz: string;
        /** Titel der Bausteine, die an der Antwort haengen. */
        bausteine: string[];
        /** Noch zu klaerende Merkmale einschliesslich dieses. */
        offen: number;
    }
    | { art: 'vorgaben'; fragen: OffeneFrage[] }
    | {
        art: 'luecken_gruppe';
        gruppe: PlatzhalterGruppe;
        label: string;
        fragen: OffeneFrage[];
        /** Vorbelegung je `verweis` — was ein Projektdatum inzwischen fuellt. */
        vorbelegung: Record<string, string>;
    }
    | {
        art: 'hindernis';
        kopf: KopfFelder | null;
        /** Offene Pflichtpunkte, die in dieser Sitzung uebersprungen wurden. */
        uebersprungen: number;
        /** Was sonst noch fehlt — nur in „Alle Bereiche" zu loesen. */
        rest: Hindernis[];
        kannWiederZeigen: boolean;
    }
    | { art: 'freigabe'; zusammen: Zusammenfassung; fassung: string; haken: BuchungsHaken };

/** Laufen die Automatik bzw. die KI gerade oder sind sie noch faellig? */
const automatikDran = (l: DialogLage, o: Bauordnung): boolean =>
    l.automatikLaeuft || (automatikFehlt(o) && !l.sitzung.automatikAus);

const kiDran = (l: DialogLage, o: Bauordnung): boolean =>
    l.kiLaeuft || (kiFehlt(o) && !automatikFehlt(o) && !l.sitzung.kiAus);

/** Fragt eine Karte diesen offenen Punkt (Platzhalter ausser Kopfsatz, Merkmal)? */
const hatKarte = (x: Pick<Luecke, 'verweis'>): boolean => {
    const name = platzhalterAusVerweis(x.verweis);
    return (!!name && !KOPF_PLATZHALTER.has(name)) || !!merkmalAusVerweis(x.verweis);
};

/**
 * Genau ein Schritt, in dieser Rangfolge:
 *   anlegen → Automatik/KI → freigegeben → Nur-Ansicht → „Das steht schon
 *   drin" → Neu dazu → Wer erlässt? → Merkmal klären → Verantwortliche →
 *   Gruppen → Hindernis → Freigabe.
 * Was in dieser Sitzung „Später" bekam, faellt bis zum Hindernis durch; das
 * zaehlt es und bietet „Wieder zeigen" an.
 */
export const naechsterSchritt = (l: DialogLage): DialogSchritt => {
    const o = l.bundle.ordnung;
    if (!o) return { art: 'anlegen', nurAnsicht: l.nurAnsicht };
    // Ein Buendel eines anderen Projekts (Wechsel, noch nicht neu geladen) — nichts anfangen.
    if (o.project_id !== l.projectId) return { art: 'laden' };

    if (!l.nurAnsicht && o.status === 'entwurf') {
        if (automatikDran(l, o)) return { art: 'automatik_laeuft', fehlgeschlagen: l.automatikFehlgeschlagen && !l.automatikLaeuft };
        if (kiDran(l, o)) return { art: 'ki_liest' };
    }
    if (o.status === 'freigegeben') return { art: 'freigegeben', zusammen: zusammenfassung(l.bundle, l.merkmale) };
    if (l.nurAnsicht) return { art: 'nur_ansicht', zusammen: zusammenfassung(l.bundle, l.merkmale) };

    const schluessel = uebersichtSchluessel(o);
    if (l.sitzung.uebersichtGesehen !== schluessel) {
        const u = uebersicht(l);
        if (u) return { art: 'uebersicht', uebersicht: u, schluessel };
    }

    if (l.paket && !automatikFehlt(o) && l.paket.regeln.length > 0 && !l.sitzung.neuDazuSpaeter) {
        return { art: 'neu_dazu', zeilen: neuDazuZeilen(l.paket, l.merkmale) };
    }

    const offen = [...l.bundle.luecken].filter(x => !x.erledigt_am).sort((a, b) => a.nr - b.nr);

    const erlass = offen.find(x => x.verweis === VERWEIS_ERLASSENDER);
    if (erlass && !o.erlassender_firma.trim() && !l.sitzung.erlassenderVonHand) {
        const wahl = erlassenderWahl(l.steckbrief, o);
        if (wahl.length) return { art: 'erlassender', luecke: erlass, wahl };
    }

    const klaerungen = offen.filter(x => merkmalAusVerweis(x.verweis) && !l.sitzung.klaerungSpaeter.has(x.id));
    if (klaerungen.length) {
        const luecke = klaerungen[0];
        const merkmal = merkmalAusVerweis(luecke.verweis) as BooleanMerkmal;
        const ids = l.paket?.klaeren.find(g => g.merkmal === merkmal)?.bausteine ?? [];
        return {
            art: 'klaerung',
            luecke,
            merkmal,
            frage: MERKMAL_KLAERUNG[merkmal]?.frage ?? luecke.punkt,
            kurz: MERKMAL_KLAERUNG[merkmal]?.kurz ?? merkmal,
            bausteine: ids.map(id => bausteinNachId(id)?.titel ?? id),
            offen: klaerungen.length,
        };
    }

    const fragen = offeneFragen(l.bundle);
    if (!l.sitzung.vorgabenSpaeter) {
        const mitVorgabe = vorgabeFragen(fragen);
        if (mitVorgabe.length) return { art: 'vorgaben', fragen: mitVorgabe };
    }

    for (const gruppe of GRUPPEN_FOLGE) {
        if (l.sitzung.gruppenSpaeter.has(gruppe)) continue;
        const inGruppe = gruppenFragen(fragen, gruppe);
        if (!inGruppe.length) continue;
        const vorbelegung: Record<string, string> = {};
        for (const f of inGruppe) {
            const wert = String(l.werte[f.name] ?? '').trim();
            if (wert) vorbelegung[f.verweis] = wert;
        }
        return { art: 'luecken_gruppe', gruppe, label: GRUPPEN_LABEL[gruppe] ?? gruppe, fragen: inGruppe, vorbelegung };
    }

    // Was jetzt noch offen ist und eine Karte haette, wurde uebersprungen.
    // Offene Pflichtpunkte ohne Karte (von Hand angelegt) stehen beim Rest.
    const kopf = kopfFelder(l);
    const pflicht = offen.filter(x => x.pflicht && !KOPF_VERWEISE.has(x.verweis));
    const uebersprungen = pflicht.filter(hatKarte).length;
    const rest: Hindernis[] = [
        ...pflicht.filter(x => !hatKarte(x)).map(x => ({ bereich: 'offene_punkte' as const, text: `Offener Pflichtpunkt Nr. ${x.nr}: ${x.punkt}` })),
        ...restHindernisse(l.bundle, l.merkmale),
    ];
    if (kopf || rest.length || uebersprungen) {
        return { art: 'hindernis', kopf, uebersprungen, rest, kannWiederZeigen: hatUebersprungen(l.sitzung) };
    }
    // Netz: die Freigabe-Karte nur, wenn auch die Liste des Moduls leer ist —
    // der Dienst prueft sie beim Freigeben nicht noch einmal.
    const alle = freigabeHindernisse(l.bundle, l.merkmale);
    if (alle.length) return { art: 'hindernis', kopf: null, uebersprungen: 0, rest: alle, kannWiederZeigen: false };

    return {
        art: 'freigabe',
        zusammen: zusammenfassung(l.bundle, l.merkmale),
        fassung: String(o.version_label ?? '').trim() || 'Rev. 00',
        haken: buchungsHaken(o, l.buchung.services, l.buchung.darfBuchen),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// FORTSCHRITT
// ═══════════════════════════════════════════════════════════════════════════

export type DialogAbschnitt = 'entwurf' | 'offene_punkte' | 'freigabe';

export const DIALOG_ABSCHNITTE: readonly DialogAbschnitt[] = ['entwurf', 'offene_punkte', 'freigabe'];

export const DIALOG_ABSCHNITT_LABEL: Record<DialogAbschnitt, string> = {
    entwurf: 'Entwurf',
    offene_punkte: 'Offene Punkte',
    freigabe: 'Freigabe',
};

export type AbschnittStand = 'erledigt' | 'aktuell' | 'offen';

/** Zu welchem Abschnitt ein Schritt gehoert. Laden und Nur-Ansicht ordnet `fortschritt` aus den Daten zu. */
export const abschnittVon = (s: DialogSchritt): DialogAbschnitt | null => {
    switch (s.art) {
        case 'anlegen':
        case 'automatik_laeuft':
        case 'ki_liest':
        case 'uebersicht':
        case 'neu_dazu':
            return 'entwurf';
        case 'erlassender':
        case 'klaerung':
        case 'vorgaben':
        case 'luecken_gruppe':
        case 'hindernis':
            return 'offene_punkte';
        case 'freigabe':
        case 'freigegeben':
            return 'freigabe';
        default:
            return null;
    }
};

/**
 * Die Leiste: erledigt nach den DATEN (Entwurf: Automatik und KI durch und
 * die Uebersicht gesehen; Offene Punkte: kein Pflichtpunkt offen; Freigabe:
 * freigegeben), aktuell ist der Abschnitt der Karte.
 */
export const fortschritt = (s: DialogSchritt, l: DialogLage): { abschnitt: DialogAbschnitt; stand: AbschnittStand }[] => {
    const o = l.bundle.ordnung;
    const freigegeben = o?.status === 'freigegeben';
    const entwurf = !!o && (freigegeben || (!automatikFehlt(o) && (!kiFehlt(o) || l.sitzung.kiAus)
        && l.sitzung.uebersichtGesehen === uebersichtSchluessel(o)));
    // Der Reihe nach: ohne fertigen Entwurf gibt es noch keine offenen Punkte, die erledigt sein koennten.
    const erledigt: Record<DialogAbschnitt, boolean> = {
        entwurf,
        offene_punkte: entwurf && (freigegeben || !l.bundle.luecken.some(x => x.pflicht && !x.erledigt_am)),
        freigabe: freigegeben,
    };
    const jetzt = abschnittVon(s) ?? DIALOG_ABSCHNITTE.find(a => !erledigt[a]) ?? 'freigabe';
    return DIALOG_ABSCHNITTE.map(abschnitt => ({
        abschnitt,
        stand: freigegeben ? 'erledigt' : abschnitt === jetzt ? 'aktuell' : erledigt[abschnitt] ? 'erledigt' : 'offen',
    }));
};
